import React, { useState, useEffect } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Home, FileText, BarChart3, Calendar, ExternalLink, ListTodo, ClipboardList, Package, X, MessageSquare, Users, LayoutDashboard, CheckSquare, FileCheck, Building2, Gauge, Car, Wrench, ShoppingCart, TrendingUp, MapPin, ChevronDown, Mail } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inklapbare sectie-header — alleen gebruikt in de managers/admins-weergave.
// Open-state leeft puur in-memory (GEEN localStorage: iOS WebKit negeert
// localStorage-writes stil → onbetrouwbaar voor UI-state).
const SectionToggle = ({ label, isOpen, onToggle, testId }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={isOpen}
    data-testid={testId}
    className="w-full flex items-center justify-between gap-2 text-xs text-gray-400 uppercase tracking-wide mb-2 mt-6 px-1 py-2 rounded-lg hover:text-white hover:bg-tw-blue-light transition-colors min-h-[36px]"
  >
    <span>{label}</span>
    <ChevronDown
      className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
    />
  </button>
);

const Sidebar = ({ locaties, selectedLocatie, onLocatieChange, isOpen, onClose, isMobile }) => {
  const location = useLocation();
  const { isAdmin, canManage, isBedrijfsleider, user, hasLocationAccess } = useAuth();
  const [openSchadeCount, setOpenSchadeCount] = useState(0);

  // Default open-states voor managers/admins: "Operatie — alle locaties" open,
  // "Eén locatie" en "Beheer — instellingen" dicht. Berekend bij laden, niet
  // gepersisteerd. Non-managers gebruiken deze state niet (platte weergave).
  const [openSections, setOpenSections] = useState({
    eenLocatie: false,
    operatie: true,
    beheer: false,
  });

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (!selectedLocatie) return;
    const token = getAccessToken();
    // 4.3: open = nieuw + in behandeling, anders verdwijnt de badge zodra
    // iemand een melding oppakt.
    fetch(`${API}/schademeldingen/aantallen?locatie_id=${selectedLocatie}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => setOpenSchadeCount(data?.open || 0))
      .catch(() => setOpenSchadeCount(0));
  }, [selectedLocatie]);

  // '/' en '/management' mogen alleen exact matchen (4.9): met startsWith was
  // het Dashboard op élke /management/*-pagina actief.
  const ALLEEN_EXACT = ['/', '/management'];
  const isItemActive = (path) =>
    ALLEEN_EXACT.includes(path)
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + '/');

  // "Eén locatie"
  const navItems = [
    { path: '/', icon: Home, label: 'Overzicht', pageKey: 'overzicht' },
    // 4.8d: locatie-omzet is niet voor medewerkers
    ...(user?.rol === 'medewerker'
      ? []
      : [{ path: '/performance', icon: TrendingUp, label: 'Performance', pageKey: 'performance' }]),
    { path: '/locatie-info', icon: FileText, label: 'Locatie Info', pageKey: 'locatie-info' },
    { path: '/verbruik', icon: Gauge, label: 'Verbruik', pageKey: 'verbruik' },
    { path: '/schademeldingen', icon: Car, label: 'Schademeldingen', pageKey: 'schademeldingen' },
    { path: '/checklists', icon: ClipboardList, label: 'Checklists', pageKey: 'checklists' },
    { path: '/bestellingen', icon: ShoppingCart, label: 'Bestellingen', pageKey: 'bestellingen' },
    { path: '/rusttaken', icon: Wrench, label: 'Rusttaken', pageKey: 'rusttaken' },
    { path: '/werkprogramma', icon: ListTodo, label: 'Werkprogramma', pageKey: 'werkprogramma' },
  ];

  // "Beheer — instellingen" (admin + manager)
  const beheerItems = [
    { path: '/beheer/producten', icon: Package, label: 'Productcatalogus', pageKey: 'productcatalogus' },
    { path: '/beheer/leveranciers', icon: Building2, label: 'Leveranciersbeheer', pageKey: 'leveranciersbeheer' },
    { path: '/beheer/taken', icon: ClipboardList, label: 'Takenbeheer', pageKey: 'takenbeheer' },
    { path: '/beheer/berichten', icon: MessageSquare, label: 'Berichtenbeheer', pageKey: 'berichtenbeheer' },
    { path: '/beheer/gebruikers', icon: Users, label: 'Gebruikersbeheer', pageKey: 'gebruikersbeheer' },
    { path: '/beheer/checklist-templates', icon: ClipboardList, label: 'Checklist Templates', pageKey: 'checklist-templates' },
    { path: '/beheer/email', icon: Mail, label: 'E-mailinstellingen', pageKey: 'email-instellingen' },
  ];

  // "Operatie — alle locaties" (admin + manager)
  const managementItems = [
    { path: '/management', icon: LayoutDashboard, label: 'Dashboard', pageKey: 'dashboard' },
    { path: '/management/performance', icon: TrendingUp, label: 'Performance', pageKey: 'performance-overzicht' },
    { path: '/management/implementaties', icon: MapPin, label: 'Implementaties', pageKey: 'implementaties' },
    { path: '/management/voorraad', icon: Package, label: 'Voorraad overzicht', pageKey: 'voorraad-overzicht' },
    { path: '/management/verbruik', icon: Gauge, label: 'Verbruik overzicht', pageKey: 'verbruik-overzicht' },
    { path: '/management/schademeldingen', icon: Car, label: 'Schademeldingen', pageKey: 'schademeldingen-overzicht' },
    { path: '/management/checklists', icon: ClipboardList, label: 'Checklists overzicht', pageKey: 'checklists-overzicht' },
    { path: '/management/rusttaken', icon: Wrench, label: 'Rusttaken overzicht', pageKey: 'rusttaken-overzicht' },
    { path: '/management/bestellingen', icon: ShoppingCart, label: 'Bestellingen overzicht', pageKey: 'bestellingen-overzicht' },
    { path: '/management/taken', icon: CheckSquare, label: 'Taken overzicht', pageKey: 'taken-overzicht' },
    { path: '/management/contracten', icon: FileCheck, label: 'Contracten overzicht', pageKey: 'contracten-overzicht' },
  ];

  const externalLinks = [
    { url: 'https://daily-ops.nl', icon: BarChart3, label: 'Daily-Ops', color: 'text-tw-gold' },
    { url: 'https://smart-roster.nl', icon: Calendar, label: 'Rooster', color: 'text-tw-gold' },
  ];

  const handleNavClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  // Filter locaties based on user access
  const filteredLocaties = React.useMemo(() => {
    if (!user) return [];
    if (isAdmin()) return locaties;
    return locaties.filter(loc => hasLocationAccess(loc._id));
  }, [user, locaties, isAdmin, hasLocationAccess]);

  // "Eén locatie"-items respecteren de bestaande bestellingen-gating
  // (alleen zichtbaar voor bedrijfsleider en hoger).
  const eenLocatieItems = navItems.filter(
    item => item.path !== '/bestellingen' || isBedrijfsleider()
  );

  const renderNavLink = (item) => (
    <Link
      key={item.path}
      to={item.path}
      onClick={handleNavClick}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-colors min-h-[44px] ${
        isItemActive(item.path)
          ? 'bg-tw-gold text-tw-blue font-medium'
          : 'text-gray-200 hover:bg-tw-blue-light hover:text-white active:bg-tw-blue-light'
      }`}
    >
      <item.icon className="w-5 h-5" />
      <span>{item.label}</span>
      {item.path === '/schademeldingen' && openSchadeCount > 0 && (
        <span className="ml-auto bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 rounded-full flex items-center justify-center px-1.5" data-testid="schade-badge">
          {openSchadeCount}
        </span>
      )}
    </Link>
  );

  if (!isOpen) return null;

  const isManagerView = canManage();

  return (
    <div className="w-64 bg-tw-blue text-white flex flex-col h-screen">
      {/* Header with Logo */}
      <div className="p-5 border-b border-tw-blue-light flex items-center justify-between">
        <div className="flex items-center">
          <img 
            src="/logo-white.png" 
            alt="Truckwash1 Group" 
            className="h-8 w-auto max-w-[140px] object-contain"
          />
        </div>
        {isMobile && (
          <button 
            onClick={onClose}
            className="p-2 hover:bg-tw-blue-light rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Locatie Selector */}
      <div className="p-4 border-b border-tw-blue-light">
        <label className="text-xs text-gray-300 uppercase tracking-wide mb-2 block">
          Locatie
        </label>
        <Select value={selectedLocatie} onValueChange={onLocatieChange}>
          <SelectTrigger className="w-full bg-tw-blue-light border-tw-blue-light text-white min-h-[44px] focus:ring-tw-gold focus:border-tw-gold">
            <SelectValue placeholder="Selecteer locatie" />
          </SelectTrigger>
          <SelectContent className="bg-tw-blue border-tw-blue-light max-h-[300px]">
            {filteredLocaties.map((loc) => (
              <SelectItem 
                key={loc._id} 
                value={loc._id}
                className="text-white hover:text-white focus:text-white hover:bg-tw-blue-light focus:bg-tw-blue-light data-[highlighted]:bg-tw-blue-light data-[highlighted]:text-white min-h-[44px] cursor-pointer"
              >
                {loc.naam}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {isManagerView ? (
          <>
            {/* Eén locatie — inklapbaar, default dicht */}
            <SectionToggle
              label="Eén locatie"
              isOpen={openSections.eenLocatie}
              onToggle={() => toggleSection('eenLocatie')}
              testId="sidebar-toggle-een-locatie"
            />
            {openSections.eenLocatie && (
              <div data-testid="sidebar-section-een-locatie">
                {eenLocatieItems.map(renderNavLink)}
              </div>
            )}

            {/* Operatie — alle locaties — inklapbaar, default open */}
            <SectionToggle
              label="Operatie — alle locaties"
              isOpen={openSections.operatie}
              onToggle={() => toggleSection('operatie')}
              testId="sidebar-toggle-operatie"
            />
            {openSections.operatie && (
              <div data-testid="sidebar-section-operatie">
                {managementItems.map(renderNavLink)}
              </div>
            )}

            {/* Beheer — instellingen — inklapbaar, default dicht */}
            <SectionToggle
              label="Beheer — instellingen"
              isOpen={openSections.beheer}
              onToggle={() => toggleSection('beheer')}
              testId="sidebar-toggle-beheer"
            />
            {openSections.beheer && (
              <div data-testid="sidebar-section-beheer">
                {beheerItems.map(renderNavLink)}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Platte weergave voor medewerker/bedrijfsleider — geen inklap-chrome */}
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-2" data-testid="sidebar-section-een-locatie">
              Eén locatie
            </p>
            {eenLocatieItems.map(renderNavLink)}
          </>
        )}

        {/* Snelkoppelingen — altijd zichtbaar */}
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-2 mt-6">
          Snelkoppelingen
        </p>
        {externalLinks.map((link) => (
          <a
            key={link.url}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleNavClick}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 hover:bg-tw-blue-light active:bg-tw-blue-light transition-colors min-h-[44px] ${link.color}`}
          >
            <link.icon className="w-5 h-5" />
            <span>{link.label}</span>
            <ExternalLink className="w-4 h-4 ml-auto" />
          </a>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-tw-blue-light text-xs text-gray-400">
        © 2026 Truckwash1 Group
      </div>
    </div>
  );
};

export default Sidebar;
